import React from 'react';
import { throttle } from 'lodash';
import { useSelector } from 'react-redux';
import InfiniteScroll from 'react-infinite-scroller';
import { SwitchTransition } from 'react-transition-group';
import { useCurrentStateAndParams } from '@wix/tpa-router/react';

import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';

import { useController } from 'common/context/controller';

import { Fade } from 'wui/Fade';
import { List } from 'wui/List';
import { Show } from 'wui/Show';
import { Divider } from 'wui/Divider';
import { EmptyState } from 'wui/EmptyState';
import { ErrorState } from 'wui/ErrorState';
import { CardContent } from 'wui/CardContent';

import { selectMembers, selectMembersMetadata } from 'store/selectors';

import { MemberListItem, MemberListItemSkeleton } from './MemberListItem';

interface IGroupMembersProps {
  groupId: string;
  'data-hook'?: string;
}

export function GroupMembers(props: IGroupMembersProps) {
  const { groupId } = props;

  const { t } = useTranslation();
  const { isMobile } = useEnvironment();
  const { params } = useCurrentStateAndParams();
  const { members$ } = useController();

  const meta = useSelector(selectMembersMetadata);
  const members = useSelector(selectMembers);

  const handleLoadMore = React.useMemo(
    () => throttle(() => members$.query(groupId, params, members.length), 800),
    [members.length],
  );

  return (
    <CardContent data-hook={props['data-hook']}>
      <SwitchTransition>
        {(() => {
          if (meta.error) {
            return (
              <Fade key="error">
                <ErrorState
                  wired
                  variant="page-borderless"
                  onRetry={handleRetry}
                  title={t('groups-web.error-state.title')}
                  actionLabel={t('groups-web.error-state.retry.label')}
                  data-hook="error-state"
                />
              </Fade>
            );
          }

          if (meta.loading) {
            return (
              <Fade key="loading">
                <List
                  disablePadding
                  separator={<Divider />}
                  gap={isMobile ? 'SP3' : 'SP5'}
                  data-hook="members-list"
                >
                  {new Array(10).fill(0).map((_, index) => (
                    <MemberListItemSkeleton
                      key={index}
                      data-hook="member-list-item-skeleton"
                    />
                  ))}
                </List>
              </Fade>
            );
          }

          if (!members.length) {
            return (
              <Fade key="empty-state">
                <EmptyState
                  wired
                  variant="page-borderless"
                  title={t('group-web.members-page.empty.title')}
                  subtitle={t('group-web.members-page.empty.description')}
                  data-hook="empty-state"
                />
              </Fade>
            );
          }

          return (
            <Fade key="members-list">
              <InfiniteScroll
                useWindow
                initialLoad={false}
                loadMore={handleLoadMore}
                hasMore={meta.metadata.total! > members.length}
              >
                <List
                  disablePadding
                  separator={<Divider />}
                  gap={isMobile ? 'SP3' : 'SP5'}
                  data-hook="members-list"
                >
                  {members.map((member) => (
                    <MemberListItem
                      groupId={groupId}
                      member={member}
                      key={member.siteMemberId}
                      data-hook="member-list-item"
                    />
                  ))}

                  <Show if={meta.fetchMore}>
                    {new Array(10).fill(0).map((_, index) => (
                      <MemberListItemSkeleton
                        key={index}
                        data-hook="member-list-item-skeleton"
                      />
                    ))}
                  </Show>
                </List>
              </InfiniteScroll>
            </Fade>
          );
        })()}
      </SwitchTransition>
    </CardContent>
  );

  function handleRetry() {
    members$.query(groupId, params);
  }
}

GroupMembers.displayName = 'GroupMembers';
